import React, { useEffect, useRef, useState } from 'react';
import { Bold, Italic, Smile } from 'lucide-react';
import './WhatsAppFormattedEditor.css';
import {
  COMMON_WHATSAPP_EMOJIS,
  findUnpairedWhatsAppFormatMarkers,
  hasWhatsAppInlineFormatting,
  htmlToWhatsappMarkup,
  whatsappMarkupToHtml,
} from './whatsappTextFormatting';

type Props = {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
  placeholder?: string;
  required?: boolean;
};

export default function WhatsAppFormattedTextarea({
  value,
  onChange,
  rows = 4,
  className = '',
  placeholder,
  required,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const minHeight = `${Math.max(rows * 1.5, 6)}rem`;

  const syncFromEditor = () => {
    const el = editorRef.current;
    if (!el) return;
    const markup = htmlToWhatsappMarkup(el.innerHTML);
    onChange(markup);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertLineBreak');
    syncFromEditor();
  };

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (!el.innerHTML || el.innerHTML === '<br>') {
      el.innerHTML = whatsappMarkupToHtml(value) || '<br>';
    }
  }, []);

  useEffect(() => {
    const el = editorRef.current;
    if (!el || document.activeElement === el) return;
    const current = htmlToWhatsappMarkup(el.innerHTML);
    if (current !== value) {
      el.innerHTML = whatsappMarkupToHtml(value) || '<br>';
    }
  }, [value]);

  const applyFormat = (command: 'bold' | 'italic') => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand(command, false);
    syncFromEditor();
  };

  const insertEmoji = (emoji: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertText', false, emoji);
    syncFromEditor();
    setEmojiOpen(false);
  };

  const toolbarBtn =
    'inline-flex items-center justify-center rounded-md border border-gray-200 p-1.5 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800';

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1">
        <button type="button" className={toolbarBtn} title="Bold" onClick={() => applyFormat('bold')}>
          <Bold className="h-4 w-4" />
        </button>
        <button type="button" className={toolbarBtn} title="Italic" onClick={() => applyFormat('italic')}>
          <Italic className="h-4 w-4" />
        </button>
        <div className="relative">
          <button
            type="button"
            className={toolbarBtn}
            title="Insert emoji"
            onClick={() => setEmojiOpen((o) => !o)}
          >
            <Smile className="h-4 w-4" />
          </button>
          {emojiOpen ? (
            <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-600 dark:bg-gray-900">
              <div className="grid grid-cols-8 gap-1">
                {COMMON_WHATSAPP_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="rounded p-1 text-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Bold/italic save as * and _ in the template. Vedika preview hides markers; Interakt shows the raw text (e.g. link*to*).
        </span>
      </div>

      {findUnpairedWhatsAppFormatMarkers(value) ? (
        <p className="text-xs text-amber-700 dark:text-amber-300">{findUnpairedWhatsAppFormatMarkers(value)}</p>
      ) : hasWhatsAppInlineFormatting(value) ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Formatted words are stored with * or _ — they will look normal in preview but appear with markers on Interakt until Meta approves.
        </p>
      ) : null}

      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-required={required || undefined}
        data-placeholder={placeholder}
        data-empty={!value.trim() ? 'true' : 'false'}
        className={`wa-formatted-editor whitespace-pre-wrap break-words ${className}`}
        style={{ minHeight }}
        onKeyDown={handleKeyDown}
        onInput={syncFromEditor}
        onBlur={syncFromEditor}
      />
    </div>
  );
}
